import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';

import { ROUTES } from '@/config/routes';

import { DASHBOARD_TABS } from './dashboardTabs';

const SeoAgentDashboard = lazy(() =>
  import('./components/SeoAgentDashboard').then((m) => ({ default: m.SeoAgentDashboard })),
);

const DashboardTab = lazy(() =>
  import('./components/DashboardTab').then((m) => ({ default: m.DashboardTab })),
);

const StrategyTab = lazy(() =>
  import('./components/StrategyTab').then((m) => ({ default: m.StrategyTab })),
);

const ActivityTaskDetailPage = lazy(() =>
  import('./components/ActivityTaskDetailPage').then((m) => ({
    default: m.ActivityTaskDetailPage,
  })),
);

const TaskPageChangesPage = lazy(() =>
  import('./components/TaskPageChangesPage').then((m) => ({ default: m.TaskPageChangesPage })),
);

const KeywordsTab = lazy(() =>
  import('./components/KeywordsTab').then((m) => ({ default: m.KeywordsTab })),
);

const ContentTab = lazy(() =>
  import('./components/ContentTab').then((m) => ({ default: m.ContentTab })),
);

const ActivityTab = lazy(() =>
  import('./components/ActivityTab').then((m) => ({ default: m.ActivityTab })),
);

const AnalyticsTab = lazy(() =>
  import('./components/AnalyticsTab').then((m) => ({ default: m.AnalyticsTab })),
);

const BrandReviewsTab = lazy(() =>
  import('./components/BrandReviewsTab').then((m) => ({ default: m.BrandReviewsTab })),
);

const CustomContentPage = lazy(() =>
  import('./components/CustomContentPage').then((m) => ({ default: m.CustomContentPage })),
);

const CustomContentDetailPage = lazy(() =>
  import('./components/CustomContentDetailPage').then((m) => ({
    default: m.CustomContentDetailPage,
  })),
);

const SeoSettingsPage = lazy(() =>
  import('./components/SeoSettingsPage').then((m) => ({ default: m.SeoSettingsPage })),
);

const SeoTabPending = lazy(() =>
  import('./components/SeoTabPending').then((m) => ({ default: m.SeoTabPending })),
);

// Its own chunk: the form carries react-hook-form, its resolver and a zod schema that the
// dashboard, which most visits end at, has no use for.
const SeoWelcomePage = lazy(() =>
  import('./routes/SeoWelcomePage').then((m) => ({ default: m.SeoWelcomePage })),
);

const SeoSetupPage = lazy(() =>
  import('./routes/SeoSetupPage').then((m) => ({ default: m.SeoSetupPage })),
);

/** The tab segments that have their own route above, so no placeholder is made for them. */
const BUILT_TABS: ReadonlySet<string> = new Set([
  '',
  'strategy',
  'keywords',
  'content',
  'activity',
  'analytics',
  'brand-reviews',
]);

/**
 * The SEO Agent screens.
 *
 * <p>`/agents/seo` is the dashboard: a layout that reads the plan once and renders a tab in its
 * outlet — the Overview at the bare path, the rest at `/agents/seo/<tab>`. The welcome page and
 * the setup are siblings, not children, and win over the tab pattern because React Router ranks a
 * static segment above a dynamic one.
 *
 * <p>Listed inside the `<ProductRoute />` group in `app/router`, ahead of `placeholderRoutes`, for
 * the reasons `features/emailWarmup/index.tsx` gives. Every path, including the tab pattern, is
 * registered in `app/navigation/screens.ts` with `seo_ai_agent` and listed in the placeholder's
 * `BUILT_ROUTES`.
 */
export const seoAgentRoutes: RouteObject[] = [
  {
    path: ROUTES.seoAgent,
    element: <SeoAgentDashboard />,
    children: [
      { index: true, element: <DashboardTab /> },
      { path: 'strategy', element: <StrategyTab /> },
      { path: 'keywords', element: <KeywordsTab /> },
      { path: 'content', element: <ContentTab /> },
      { path: 'activity', element: <ActivityTab /> },
      { path: 'analytics', element: <AnalyticsTab /> },
      { path: 'brand-reviews', element: <BrandReviewsTab /> },
      // Create page or post, and one request of it, opened from the Content tab.
      { path: 'create', element: <CustomContentPage /> },
      { path: 'custom-content/:runId', element: <CustomContentDetailPage /> },
      // Tabs not ported yet. Each moves to its own route as its screen arrives.
      ...DASHBOARD_TABS.filter((tab) => !BUILT_TABS.has(tab.segment)).map((tab) => ({
        path: tab.segment,
        element: <SeoTabPending label={tab.label} />,
      })),
      // One task's detail page, opened from the Overview's "View task" and its activity rows.
      { path: 'activity-task/:taskId', element: <ActivityTaskDetailPage /> },
      // One page the task's latest run changed: its settings and text, before and after.
      { path: 'activity-task/:taskId/changes/:postId', element: <TaskPageChangesPage /> },
      // The agent's settings, opened from "Manage agent" and the "Manage connections" links.
      { path: 'settings', element: <SeoSettingsPage /> },
    ],
  },
  { path: ROUTES.seoAgentWelcome, element: <SeoWelcomePage /> },
  { path: ROUTES.seoAgentSetup, element: <SeoSetupPage /> },
];

export { useGscTrend as useSeoGscTrend } from './hooks/useDashboardReads';
export { useDashboardSummary as useSeoDashboardSummary } from './hooks/useDashboardSummary';
export { useSite as useSeoSite } from './hooks/useSite';
export { useSeoSetupStep } from './setupProgress';
export type { SeoDashboardSummary } from './hooks/useDashboardSummary';
